/**
 * Servicio para gestión de ingresos varios
 * Registra ingresos no relacionados con venta de ganado
 *
 * NOTA: Este servicio ahora usa el patrón Repository via DatabaseFactory.
 * Esto permite desacoplar la lógica de negocio de la implementación específica
 * de base de datos (Dexie para web, SQLite para mobile).
 */

import { contraparteConValor } from '../contraparte/contraparteSearch';
import {
  Ingreso,
  IngresoDTO,
  FiltroIngresos
} from '../../types/administracion';
import { getRepositories } from '../storage';

/**
 * Servicio de Ingresos
 *
 * Usa el repositorio via factory para operaciones de datos.
 */
export class IngresoService {
  /**
   * Obtiene el repositorio de ingresos
   */
  private static getRepo() {
    const { ingreso } = getRepositories();
    return ingreso;
  }

  /**
   * Crea un nuevo ingreso
   */
  static async create(ingresoDTO: IngresoDTO & {
    empresaId?: string;
    userId?: string;
  }): Promise<string> {
    return this.registrarIngreso(ingresoDTO);
  }

  /**
   * Alias para create() - usado por componentes
   */
  static async registrarIngreso(ingresoDTO: IngresoDTO & {
    empresaId?: string;
    userId?: string;
    concepto_id?: string;
  }): Promise<string> {
    try {
      const { profile } = getRepositories();

      // Get empresa_id and user_id from profiles if not provided
      let empresaId = ingresoDTO.empresaId;
      let userId = ingresoDTO.userId;

      if (!empresaId || !userId) {
        const userInfo = await profile.getCurrentUserInfo();
        empresaId = empresaId || userInfo.empresaId;
        userId = userId || userInfo.userId;
      }

      if (!empresaId) {
        throw new Error('No active empresa found');
      }

      if (!userId) {
        throw new Error('User ID not available');
      }

      const fechaActual = new Date().toISOString();

      const ingreso: Omit<Ingreso, 'id'> = {
        updated_at: fechaActual,
        fecha_evento: ingresoDTO.fecha_evento,
        concepto: ingresoDTO.concepto,
        descripcion: ingresoDTO.descripcion,
        monto: ingresoDTO.monto,
        categoria: ingresoDTO.categoria,
        concepto_id: ingresoDTO.concepto_id ?? undefined,
        // De quién se cobró (2026-10-18): opcional, sólo viaja con valor.
        ...contraparteConValor(ingresoDTO),
        empresa_id: empresaId,
        user_id: userId,
        is_deleted: false
      };

      // Guardar usando repositorio
      const ingresoId = await this.getRepo().add(ingreso);

      return ingresoId;
    } catch (error) {
      console.error('Error creando ingreso:', error);
      throw error;
    }
  }

  /**
   * Obtiene todos los ingresos de la empresa activa
   */
  static async getAll(): Promise<Ingreso[]> {
    try {
      return await this.getRepo().getAllForActiveEmpresa();
    } catch (error) {
      console.error('IngresoService.getAll error:', error);
      throw error;
    }
  }

  /**
   * Obtiene un ingreso por ID
   */
  static async getById(id: string): Promise<Ingreso | undefined> {
    try {
      return await this.getRepo().get(id);
    } catch (error) {
      console.error('IngresoService.getById error:', error);
      throw error;
    }
  }

  /**
   * Actualiza un ingreso
   */
  static async update(id: string, updates: Partial<Ingreso>): Promise<void> {
    try {
      const existente = await this.getById(id);
      if (!existente) {
        throw new Error('Ingreso no encontrado');
      }

      await this.getRepo().update({ ...updates, id });
    } catch (error) {
      console.error('Error actualizando ingreso:', error);
      throw error;
    }
  }

  /**
   * Elimina un ingreso (soft delete)
   */
  static async delete(id: string): Promise<void> {
    try {
      const ingreso = await this.getById(id);
      if (!ingreso) {
        throw new Error('Ingreso no encontrado');
      }
      await this.getRepo().softDelete(id);
    } catch (error) {
      console.error('Error eliminando ingreso:', error);
      throw error;
    }
  }

  /**
   * Obtiene ingresos por concepto
   */
  static async getByConcepto(concepto: string): Promise<Ingreso[]> {
    return await this.getRepo().searchByConcepto(concepto);
  }

  /**
   * Obtiene ingresos por categoría
   */
  static async getByCategoria(categoria: string): Promise<Ingreso[]> {
    return await this.getRepo().getByCategoria(categoria);
  }

  /**
   * Obtiene ingresos por período
   */
  static async getByPeriodo(
    fechaInicio: string,
    fechaFin: string
  ): Promise<Ingreso[]> {
    try {
      return await this.getRepo().getByPeriodo(fechaInicio, fechaFin);
    } catch (error) {
      console.error('IngresoService.getByPeriodo error:', error);
      throw error;
    }
  }

  /**
   * Obtiene ingresos con filtros
   */
  static async getByFiltros(filtros: FiltroIngresos): Promise<Ingreso[]> {
    return await this.getRepo().getByFiltros(filtros);
  }

  /**
   * Obtiene estadísticas de ingresos para un período
   */
  static async getEstadisticas(
    fechaInicio: string,
    fechaFin: string
  ): Promise<{
    total: number;
    promedio: number;
    cantidad: number;
    conceptoMasUsado: string | null;
    montoMaximo: number;
    montoMinimo: number;
  }> {
    const ingresos = await this.getByPeriodo(fechaInicio, fechaFin);

    if (ingresos.length === 0) {
      return {
        total: 0,
        promedio: 0,
        cantidad: 0,
        conceptoMasUsado: null,
        montoMaximo: 0,
        montoMinimo: 0
      };
    }

    const total = ingresos.reduce((sum, i) => sum + i.monto, 0);
    const promedio = total / ingresos.length;

    // Concepto más usado
    const conceptos = new Map<string, number>();
    ingresos.forEach(i => {
      conceptos.set(i.concepto, (conceptos.get(i.concepto) || 0) + 1);
    });
    const conceptoMasUsado = Array.from(conceptos.entries())
      .sort((a, b) => b[1] - a[1])[0]?.[0] || null;

    const montos = ingresos.map(i => i.monto);
    const montoMaximo = Math.max(...montos);
    const montoMinimo = Math.min(...montos);

    return {
      total,
      promedio,
      cantidad: ingresos.length,
      conceptoMasUsado,
      montoMaximo,
      montoMinimo
    };
  }

  /**
   * Obtiene los top N conceptos por monto
   */
  static async getTopConceptos(limit: number = 5): Promise<Array<{
    concepto: string;
    total: number;
    cantidad: number;
  }>> {
    const ingresos = await this.getAll();
    const conceptos = new Map<string, { total: number; cantidad: number }>();

    ingresos.forEach(i => {
      const actual = conceptos.get(i.concepto) || { total: 0, cantidad: 0 };
      conceptos.set(i.concepto, {
        total: actual.total + i.monto,
        cantidad: actual.cantidad + 1
      });
    });

    return Array.from(conceptos.entries())
      .map(([concepto, data]) => ({ concepto, ...data }))
      .sort((a, b) => b.total - a.total)
      .slice(0, limit);
  }
}

export default IngresoService;
